import { message } from 'ant-design-vue'
import JsPdf from 'jspdf'

import { fontFamilyMap } from './pdf-draw'

export type FontKey =
  | 'msyh'
  | 'simsun'
  | 'simhei'
  | 'uighur'
  | 'sarabun'
  | 'ben'
  | 'kor'
  | 'bur'
  | 'km'
  | 'ar'
/** 字体信息 */
interface FontInfo {
  /** 支持的语言 */
  supportLangs: Lang[]
  /** 字体列表 */
  fontList: {
    /** 字体文件地址 */
    url: string
    /** 字体权重 */
    style: string
    /** 字体是否已加载 */
    isLoad: boolean
  }[]
}

type Lang =
  | 'zh'
  | 'en'
  | 'kz'
  | 'vi'
  | 'th'
  | 'id'
  | 'ug'
  | 'ben'
  | 'kor'
  | 'bur'
  | 'tr'
  | 'km'
  | 'es'
  | 'ar'
export const fontToOss = new Map<FontKey, FontInfo>([
  // 微软雅黑
  [
    'msyh',
    {
      // 微软雅黑进行保底，项目的内大部分语言微软雅黑都支持
      supportLangs: ['zh', 'en', 'kz', 'vi', 'th', 'id', 'tr', 'es'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/msyh.text',
          style: 'normal',
          isLoad: false,
        },
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/msyh-bold.text',
          style: 'bold',
          isLoad: false,
        },
      ],
    },
  ],
  //   宋体
  [
    'simsun',
    {
      supportLangs: ['zh', 'en'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/simsun.text',
          style: 'normal',
          isLoad: false,
        },
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/simsun-bold.text',
          style: 'bold',
          isLoad: false,
        },
      ],
    },
  ],
  //   黑体
  [
    'simhei',
    {
      supportLangs: ['zh', 'en'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/simhei.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
  //  维语
  [
    'uighur',
    {
      supportLangs: ['ug', 'ar'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/uighur.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
  // 泰语
  [
    'sarabun',
    {
      supportLangs: ['th'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/Sarabun.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
  // 孟加拉语
  [
    'ben',
    {
      supportLangs: ['ben'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/ben.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
  // 韩语
  [
    'kor',
    {
      supportLangs: ['kor'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/kor.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
  // 缅甸语
  [
    'bur',
    {
      supportLangs: ['bur'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/bur.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
  // 柬埔寨语
  [
    'km',
    {
      supportLangs: ['km'],
      fontList: [
        {
          url: 'https://cdn.eggrj.com/cloud_paiban/print_pdf_font/km.text',
          style: 'normal',
          isLoad: false,
        },
      ],
    },
  ],
])
/** 整合一些需要处理的jspdf工具函数 */
class JsPdfTool {
  // 单例
  static instance: JsPdfTool
  font!: typeof fontToOss
  constructor() {
    if (JsPdfTool.instance) {
      return JsPdfTool.instance
    }
    JsPdfTool.instance = this
    this.font = fontToOss
  }
  /** 加载单个字体 */
  async loadFont(fontKey: FontKey, lang: Lang, style: 'normal' | 'bold') {
    try {
      const target = this.getFont(fontKey, lang, style)
      if (target.isLoad) return
      const result = await fetch(target.url).then((r) => r.text())
      const callAddFont = function (this: JsPdf) {
        this.addFileToVFS(`${fontKey}.ttf`, result)
        this.addFont(`${fontKey}.ttf`, fontKey, style)
      }
      JsPdf.API.events.push(['addFonts', callAddFont])
      target.isLoad = true
      return {
        font: result,
      }
    } catch (error) {
      message.error(`${fontKey}字体加载失败,pdf文件内容可能存在损坏,请刷新重试`)
    }
  }
  /** 获取字体 */
  getFont(fontKey: FontKey, lang: Lang, style: 'normal' | 'bold') {
    const { fontList = [], supportLangs = [] } = this.font.get(fontKey) || {}

    // 完美匹配：样式和语言都匹配
    let target = fontList.find(
      (item) => item.style === style && supportLangs.includes(lang)
    )
    // 找不到则找语言匹配的第一个样式
    if (!target && supportLangs.includes(lang)) {
      target = fontList[0]
    }
    const fallbackFont = this.font.get('msyh')
    // 如果还是找不到，使用 msyh 上样式匹配的
    if (!target) {
      target = fallbackFont?.fontList.find((item) => item.style === style)
    }
    // 字体文件地址为空（如尚未上传的语言字体）时，回退到 msyh
    if (target && !target.url) {
      target =
        fallbackFont?.fontList.find((item) => item.style === style) ??
        fallbackFont?.fontList[0]
    }
    // 最后兜底使用 msyh 的第一个字体
    return target || fallbackFont!.fontList[0]
  }
  async setFont(instance: JsPdf, fontKey: FontKey) {
    await this.loadFont(fontKey, 'zh', 'normal')
    instance.setFont(fontKey)
  }
  /** 加载标签模版内的所有字体文件 */
  async loadFontByTagTemp(
    temp: {
      data: {
        fontFamily: keyof typeof fontFamilyMap
        fontWeight: string
        /** 此数据并不是模板中的，只是为了以后可以正常处理其他类型的文字而添加的，让此代码不局限在模板 */
        lang: Lang
      }
      type: string
    }[]
  ) {
    const fonts = new Set<string>()
    // 从模版中拿到所需要的所有字体统一注册
    temp.forEach((item) => {
      const { fontFamily, fontWeight, lang } = item.data ?? {}
      const font = (fontFamilyMap[fontFamily] as FontKey) || 'msyh'
      const obj = {
        font: font,
        lang: lang ?? 'zh',
        style: fontWeight === 'bold' ? 'bold' : 'normal',
      }
      fonts.add(JSON.stringify(obj))
    })
    const promise = [...fonts].map((objStr) => {
      const { font, lang, style } = JSON.parse(objStr)

      return this.loadFont(font, lang, style)
    })
    await Promise.all(promise)
  }
}

/**
 * @description 切换 pdf 字体
 * @param {*} info
 * @param {*} pdf
 */
export function setPdfFont(
  info: { fontFamily: keyof typeof fontFamilyMap; fontWeight: string },
  pdf: JsPdf
) {
  const { fontFamily, fontWeight } = info || {}
  if (!fontFamily) return
  let font = fontFamilyMap[fontFamily]
  if (fontWeight === 'bold') {
    const bFont = (font + '-bold') as FontKey
    if (fontToOss.has(bFont)) {
      font = bFont
    }
  }
  pdf.setFont(font)
}

export const jsPdfTool = new JsPdfTool()
jsPdfTool.loadFont('uighur', 'ug', 'normal')
jsPdfTool.loadFont('sarabun', 'th', 'normal')
jsPdfTool.loadFont('ben', 'ben', 'normal')
jsPdfTool.loadFont('kor', 'kor', 'normal')
jsPdfTool.loadFont('bur', 'bur', 'normal')
jsPdfTool.loadFont('km', 'km', 'normal')
